<template>
  <div :class="skeletonClasses" :style="skeletonStyles" />
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface AppSkeletonProps {
  /** Ширина skeleton */
  width?: string | number
  /** Высота skeleton */
  height?: string | number
  /** Вариант skeleton */
  variant?: 'rectangular' | 'circular' | 'text' | 'rounded'
  /** Анимация */
  animate?: boolean
}

const props = withDefaults(defineProps<AppSkeletonProps>(), {
  variant: 'rectangular',
  animate: true,
})

const skeletonClasses = computed(() => [
  'app-skeleton',
  `app-skeleton--${props.variant}`,
  {
    'app-skeleton--animate': props.animate,
  },
])

const skeletonStyles = computed(() => {
  const styles: Record<string, string> = {}

  if (props.width) {
    styles.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }

  if (props.height) {
    styles.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }

  // Text variant имеет фиксированную высоту
  if (props.variant === 'text' && !props.height) {
    styles.height = '1rem'
  }

  return styles
})
</script>

<style scoped>
.app-skeleton {
  background-color: var(--color-bg-muted, #f4f4f5);
  display: block;
  width: 100%;
  position: relative;
  overflow: hidden;
}

/* Variants */
.app-skeleton--rectangular {
  border-radius: var(--radius-sm, 0.25rem);
}

.app-skeleton--rounded {
  border-radius: var(--radius-md, 0.375rem);
}

.app-skeleton--circular {
  border-radius: 50%;
  aspect-ratio: 1;
}

.app-skeleton--text {
  border-radius: var(--radius-sm, 0.5rem);
  height: 1rem;
  transform-origin: 0 60%;
  transform: scale(1, 0.6);
}

/* Animation */
.app-skeleton--animate {
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Wave animation variant */
.app-skeleton--animate::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: skeleton-wave 1.5s linear infinite;
}

@keyframes skeleton-wave {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Dark theme */
:global(.dark) .app-skeleton {
  background-color: var(--color-surface-variant, #334155);
}
</style>
